import React, { useEffect, useState } from 'react';
import { CompanySelectField } from './CompanySelectField';
import { PasswordInput } from './PasswordInput';
import { apiUrl, apiUnreachableMessage } from '../config/api';
import { DEFAULT_SUPER_ADMIN_COMPANY, findCompanyIdByName } from '../config/defaults';
import { getPreferredCompanyId } from '../utils/companySelection';
import { useAdminCompanies } from '../utils/useAdminCompanies';
import { refreshCurrentUser } from '../utils/refreshCurrentUser';
import { getStoredUser } from '../utils/auth';
import {
  COMPANY_ADMIN_ROLE_OPTIONS,
  INTERNAL_ROLE_OPTIONS,
  PLATFORM_ROLE_OPTIONS,
  SAFETY_SUPERVISOR_ROLE_OPTIONS,
  isPlatformAdmin,
  isSuperAdmin,
  isSuperUser,
} from '../utils/roles';

const ROLE_OPTIONS = PLATFORM_ROLE_OPTIONS;

const inputClass =
  'w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent transition';

/** Create-user form for super admin (and admin when enabled). */
export function UserSignupForm({
  onBack,
  user: userProp,
  companyName: companyNameProp,
  internalOnly = false,
  onCreated,
  backLabel = '← Back to dashboard',
}) {
  const [actor, setActor] = useState(userProp || getStoredUser());
  const actorIsSuperAdmin = isSuperAdmin(actor?.role);
  const actorIsSuperUser = isSuperUser(actor?.role);
  const actorIsPlatformAdmin = isPlatformAdmin(actor?.role);
  const roleOptions = internalOnly
    ? actorIsSuperUser
      ? SAFETY_SUPERVISOR_ROLE_OPTIONS
      : INTERNAL_ROLE_OPTIONS
    : actorIsSuperAdmin
      ? ROLE_OPTIONS
      : COMPANY_ADMIN_ROLE_OPTIONS;
  const adminCompanyName = companyNameProp || actor?.company_name || '';
  const { companies, loading: companiesLoading, error: companiesError, reload } = useAdminCompanies({
    enabled: actorIsPlatformAdmin,
  });
  const internalCompanyId = findCompanyIdByName(companies);
  const internalCompanyName =
    companies.find(
      (c) => c.name?.trim().toUpperCase() === DEFAULT_SUPER_ADMIN_COMPANY.toUpperCase()
    )?.name || DEFAULT_SUPER_ADMIN_COMPANY;
  const [formData, setFormData] = useState({
    project_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    company_id: actorIsSuperAdmin ? '' : String(actor?.company_id || ''),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userProp) setActor(userProp);
  }, [userProp]);

  useEffect(() => {
    if (!internalOnly || !actorIsSuperUser) return;
    setFormData((prev) => ({
      ...prev,
      role: SAFETY_SUPERVISOR_ROLE_OPTIONS.some((o) => o.value === prev.role)
        ? prev.role
        : 'super_visor',
    }));
  }, [internalOnly, actorIsSuperUser]);

  useEffect(() => {
    if (!internalOnly || !internalCompanyId) return;
    setFormData((prev) =>
      prev.company_id === internalCompanyId ? prev : { ...prev, company_id: internalCompanyId }
    );
  }, [internalOnly, internalCompanyId]);

  useEffect(() => {
    if (actorIsSuperAdmin || companyNameProp || actor?.company_name) return undefined;
    let cancelled = false;
    (async () => {
      const refreshed = await refreshCurrentUser(actor?.email);
      if (!cancelled && refreshed) setActor(refreshed);
    })();
    return () => {
      cancelled = true;
    };
  }, [actorIsSuperAdmin, actor?.email, actor?.company_name, companyNameProp]);

  useEffect(() => {
    if (internalOnly || actorIsSuperAdmin || !actor?.company_id) return;
    setFormData((prev) => ({
      ...prev,
      company_id: String(actor.company_id),
    }));
  }, [internalOnly, actorIsSuperAdmin, actor?.company_id]);

  useEffect(() => {
    if (internalOnly || !actorIsSuperAdmin || companiesLoading || !companies.length) return;
    setFormData((prev) => {
      if (prev.company_id) return prev;
      if (prev.role !== 'user' && prev.role !== 'admin') return prev;
      const preferred = getPreferredCompanyId(companies);
      const defaultId = preferred || findCompanyIdByName(companies);
      return defaultId ? { ...prev, company_id: defaultId } : prev;
    });
  }, [internalOnly, actorIsSuperAdmin, companies, companiesLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (internalOnly) {
        next.company_id = internalCompanyId || prev.company_id;
        return next;
      }
      if (
        name === 'role' &&
        (value === 'admin' || value === 'user') &&
        !prev.company_id
      ) {
        const preferred = getPreferredCompanyId(companies);
        const defaultId = preferred || findCompanyIdByName(companies);
        if (defaultId) next.company_id = defaultId;
      }
      if (name === 'role' && (value === 'super_admin' || value === 'super_user' || value === 'super_visor' || value === 'assistant_super_visor')) {
        next.company_id = internalCompanyId || '';
      }
      return next;
    });
  };

  const needsCompany =
    !internalOnly &&
    actorIsSuperAdmin &&
    (formData.role === 'user' || formData.role === 'admin');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (internalOnly && !internalCompanyId) {
      setError('Internal company (TOCG) is not configured');
      return;
    }
    if (needsCompany && !formData.company_id) {
      setError('Please select a company for this account');
      return;
    }
    setLoading(true);
    try {
      const body = {
        project_name: formData.project_name.trim(),
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };
      if (internalOnly) {
        body.company_id = Number(internalCompanyId);
      } else if (actorIsSuperAdmin && formData.company_id) {
        body.company_id = Number(formData.company_id);
      }
      const res = await fetch(apiUrl('/api/admin/users'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': actor?.email || '',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
        setError(msg || 'Failed to create account');
        return;
      }
      setSuccess(`User ${formData.email} created successfully.`);
      const resetRole = internalOnly
        ? actorIsSuperUser
          ? 'super_visor'
          : 'super_user'
        : 'user';
      const resetCompanyId = internalOnly
        ? internalCompanyId || ''
        : actorIsSuperAdmin
          ? getPreferredCompanyId(companies) || findCompanyIdByName(companies) || ''
          : String(actor?.company_id || '');
      setFormData({
        project_name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: resetRole,
        company_id: resetCompanyId,
      });
      onCreated?.();
    } catch {
      setError(apiUnreachableMessage());
    } finally {
      setLoading(false);
    }
  };

  const form = (
    <form onSubmit={handleSubmit} className="space-y-5">
      {success && (
        <div className="text-green-400 text-sm text-center bg-green-400/10 py-2 px-3 rounded">
          {success}
        </div>
      )}
      {error && (
        <div className="text-red-400 text-sm text-center bg-red-400/10 py-2 px-3 rounded">
          {error}
        </div>
      )}
      <div>
        <label htmlFor="project_name" className="block text-sm font-medium text-gray-300 mb-2">
          {internalOnly ? 'User Name' : 'User/Worker Name'}
        </label>
        <input
          type="text"
          id="project_name"
          name="project_name"
          value={formData.project_name}
          onChange={handleChange}
          required
          className={inputClass}
          placeholder="Display name for this user"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className={inputClass}
          placeholder="you@example.com"
        />
      </div>
      {actorIsPlatformAdmin && (
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-2">
            Role
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            className={inputClass}
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {!actorIsPlatformAdmin && (
        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-300 mb-2">
            Role
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            className={inputClass}
          >
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {actorIsSuperAdmin && needsCompany && (
        <CompanySelectField
          companies={companies}
          loading={companiesLoading}
          error={companiesError}
          value={formData.company_id}
          onChange={handleChange}
          onRefresh={reload}
          showHints={false}
        />
      )}
      {(internalOnly ||
        (actorIsPlatformAdmin &&
          (formData.role === 'super_admin' ||
            formData.role === 'super_user' ||
            formData.role === 'super_visor' ||
            formData.role === 'assistant_super_visor'))) && (
        <div>
          <label htmlFor="internal_company" className="block text-sm font-medium text-gray-300 mb-2">
            Company
          </label>
          <input
            id="internal_company"
            type="text"
            value={internalCompanyName}
            readOnly
            disabled
            className={`${inputClass} opacity-70 cursor-not-allowed`}
          />
          {internalOnly ? (
            <p className="text-xs text-gray-500 mt-1">Internal users are always linked to TOCG.</p>
          ) : null}
        </div>
      )}
      {!internalOnly && !actorIsPlatformAdmin && (
        <div>
          <label htmlFor="admin_company" className="block text-sm font-medium text-gray-300 mb-2">
            Company
          </label>
          <input
            id="admin_company"
            type="text"
            value={adminCompanyName}
            readOnly
            disabled
            className={`${inputClass} opacity-70 cursor-not-allowed`}
          />
        </div>
      )}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
          Password
        </label>
        <PasswordInput
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          minLength={8}
          className={inputClass}
          placeholder="••••••••"
          autoComplete="new-password"
        />
      </div>
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
          Confirm Password
        </label>
        <PasswordInput
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          minLength={8}
          className={inputClass}
          placeholder="••••••••"
          autoComplete="new-password"
        />
      </div>
      <button
        type="submit"
        disabled={loading || (actorIsSuperAdmin && companiesLoading && needsCompany)}
        className="w-full text-gray-900 font-semibold py-3 px-4 rounded-lg bg-[#eda448] hover:bg-[#d8932e] transition-colors disabled:opacity-70"
      >
        {loading ? 'Creating...' : internalOnly ? 'Create User' : 'Create User/Worker'}
      </button>
    </form>
  );

  return (
    <div className="max-w-md">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-400 hover:text-white mb-4 flex items-center gap-1"
        >
          {backLabel}
        </button>
      )}
      <h2 className="text-xl font-semibold text-white mb-1">
        {internalOnly ? 'Create Internal User' : 'Create User/Worker'}
      </h2>
      <p className="text-gray-400 text-xs mb-6">
        {actorIsSuperUser && internalOnly
          ? 'Add supervisors.'
          : actorIsSuperAdmin && internalOnly
            ? 'Add internal platform users.'
            : actorIsSuperAdmin
              ? 'Add User/Workers to a company.'
              : 'Add users or supervisors.'}
      </p>
      {form}
    </div>
  );
}
